/**
 * TouchHandler - Lightweight native touch/mouse swipe detection.
 * No dependencies. Used by HorizontalTimeline for swipe navigation.
 */

class TouchHandler {
    constructor(element, options = {}) {
        this.element = element;
        this.options = {
            threshold: options.threshold || 75,
            preventDefaultEvents: options.preventDefaultEvents || false,
            swipeLeft: options.swipeLeft || null,
            swipeRight: options.swipeRight || null,
            ...options
        };

        this.startX = 0;
        this.startY = 0;
        this.startTime = 0;
        this.currentX = 0;
        this.currentY = 0;
        this.isDown = false;

        // Store bound references so destroy() can actually remove them
        this._onStart = this.handleStart.bind(this);
        this._onMove = this.handleMove.bind(this);
        this._onEnd = this.handleEnd.bind(this);

        this._attach();
    }

    _attach() {
        const passive = !this.options.preventDefaultEvents;
        // Touch events
        this.element.addEventListener('touchstart', this._onStart, { passive });
        this.element.addEventListener('touchmove', this._onMove, { passive });
        this.element.addEventListener('touchend', this._onEnd, { passive });
        // Mouse events (desktop fallback)
        this.element.addEventListener('mousedown', this._onStart);
        this.element.addEventListener('mousemove', this._onMove);
        this.element.addEventListener('mouseup', this._onEnd);
        this.element.addEventListener('mouseleave', this._onEnd);
    }

    handleStart(e) {
        this.isDown = true;
        this.startTime = Date.now();
        const point = e.touches ? e.touches[0] : e;
        this.startX = point.clientX;
        this.startY = point.clientY;
        this.currentX = point.clientX;
        this.currentY = point.clientY;

        if (this.options.preventDefaultEvents) e.preventDefault();
    }

    handleMove(e) {
        if (!this.isDown) return;
        const point = e.touches ? e.touches[0] : e;
        this.currentX = point.clientX;
        this.currentY = point.clientY;

        if (this.options.preventDefaultEvents) e.preventDefault();
    }

    handleEnd(e) {
        if (!this.isDown) return;
        this.isDown = false;

        const deltaX = this.currentX - this.startX;
        const deltaY = this.currentY - this.startY;
        const absDeltaX = Math.abs(deltaX);
        const absDeltaY = Math.abs(deltaY);

        // Only trigger if horizontal movement dominates and exceeds threshold
        if (absDeltaX > absDeltaY && absDeltaX > this.options.threshold) {
            const direction = deltaX > 0 ? 'right' : 'left';
            const distance = absDeltaX;
            const duration = Date.now() - this.startTime;

            if (direction === 'left' && this.options.swipeLeft) {
                this.options.swipeLeft.call(this.element, e, 'left', distance, duration, 1);
            } else if (direction === 'right' && this.options.swipeRight) {
                this.options.swipeRight.call(this.element, e, 'right', distance, duration, 1);
            }
        }

        if (this.options.preventDefaultEvents) e.preventDefault();
    }

    destroy() {
        this.element.removeEventListener('touchstart', this._onStart);
        this.element.removeEventListener('touchmove', this._onMove);
        this.element.removeEventListener('touchend', this._onEnd);
        this.element.removeEventListener('mousedown', this._onStart);
        this.element.removeEventListener('mousemove', this._onMove);
        this.element.removeEventListener('mouseup', this._onEnd);
        this.element.removeEventListener('mouseleave', this._onEnd);
    }
}

export default TouchHandler;
